/** Zeitangaben, wie ein Messenger sie zeigt. */

const time = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
})

const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'long' })

const date = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

const dateLong = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

function daysApart(value: Date, now: Date): number {
  const a = new Date(value.getFullYear(), value.getMonth(), value.getDate())
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/** Zeitstempel in der Chatliste: heute die Uhrzeit, sonst Tag oder Datum. */
export function listTime(iso: string | null): string {
  if (!iso) return ''
  const value = new Date(iso)
  const apart = daysApart(value, new Date())
  if (apart === 0) return time.format(value)
  if (apart === 1) return 'Gestern'
  if (apart < 7) return weekday.format(value)
  return date.format(value)
}

/** Uhrzeit unter einer Sprechblase. */
export function bubbleTime(iso: string): string {
  return time.format(new Date(iso))
}

/** Beschriftung des Datumstrenners im Verlauf. */
export function dayLabel(iso: string): string {
  const value = new Date(iso)
  const apart = daysApart(value, new Date())
  if (apart === 0) return 'Heute'
  if (apart === 1) return 'Gestern'
  if (apart < 7) return weekday.format(value)
  return dateLong.format(value)
}

/** Zwei Nachrichten am selben Kalendertag? */
export function sameDay(a: string, b: string): boolean {
  const x = new Date(a)
  const y = new Date(b)
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  )
}

/**
 * Eine Dateigröße zum Anzeigen.
 *
 * Grob gerundet, und das mit Absicht: „240 MB" beantwortet die Frage, ob das
 * durch die Leitung passt. „239,7 MB" beantwortet sie auch, aber schlechter.
 * Unter einem Megabyte in Kilobyte -- ein verkleinertes Foto liegt dort, und
 * „0,4 MB" liest sich niemand gern.
 */
export function groesse(bytes: number): string {
  if (bytes >= 1024 * 1024 * 10) return `${Math.round(bytes / (1024 * 1024))} MB`
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} kB`
}

/** Initialen für den Platzhalter-Avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2)
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`
}

/** Ein Datum zum Anzeigen: „12. September". */
export function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })
}

/**
 * Der Reisezeitraum in einer Zeile.
 *
 * Wiederholt wird nur, was sich unterscheidet: „12.–18. Juni 2026" im selben
 * Monat, „28. Juni – 3. Juli 2026" im selben Jahr, sonst beide Jahreszahlen.
 * Steht nur eines der beiden Daten fest, sagt das Wort davor, welches.
 *
 * Die Werte kommen als `JJJJ-MM-TT` vom Server, ohne Zeit und ohne Zeitzone.
 * Genau deshalb wird hier von Hand zerlegt: `new Date('2026-06-12')` liest der
 * Browser als Mitternacht UTC und zeigt in westlichen Zeitzonen den Vortag.
 */
export function tripPeriod(von: string | null, bis: string | null): string {
  if (von === null && bis === null) return ''
  if (von === null) return `bis ${langesDatum(bis!)}`
  if (bis === null) return `ab ${langesDatum(von)}`
  if (von === bis) return langesDatum(von)

  const [vj, vm] = von.split('-')
  const [bj, bm] = bis.split('-')

  if (vj !== bj) return `${langesDatum(von)} – ${langesDatum(bis)}`
  if (vm !== bm) return `${tagUndMonat(von)} – ${langesDatum(bis)}`
  return `${tag(von)}.–${langesDatum(bis)}`
}

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

function tag(iso: string): string {
  return String(Number(iso.slice(8, 10)))
}

function tagUndMonat(iso: string): string {
  return `${tag(iso)}. ${MONATE[Number(iso.slice(5, 7)) - 1]}`
}

function langesDatum(iso: string): string {
  return `${tagUndMonat(iso)} ${iso.slice(0, 4)}`
}
